import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreatePayload, CrudService } from '../shared/grid/crud.service';
import { IGridService } from '../shared/grid/grid.service';
import { GridModel } from '../shared/grid/grid.model';
import { Template, TemplateJson, TemplatePrintOptions, ReportTemplateJson } from '../models/template.model';
import { ReportTemplate, UserInputField } from '../models/reportTemplate.model';
import { ReportTemplateListResponse } from '../models/reportTemplateListResponse.model';
import { ColumnDetails } from '../models/columnDetails.model';
import { GridExport } from '../models/gridExport.model';
import { TranslateService } from './translate.service';

@Injectable({
    providedIn: 'root'
})
export class ReportTemplateService extends CrudService<Template> implements IGridService<ReportTemplateListResponse> {

    constructor(http: HttpClient, private translateService: TranslateService) {
        super('ReportTemplate', http);
    }

    get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string, headers?: HttpHeaders): Observable<GridModel<ReportTemplateListResponse>> {
        return this.http.get<GridModel<ReportTemplateListResponse>>(this.getApiUrl(`${this.controllerName}/GetList`), {
            params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, filterBy),
            headers
        }).pipe(
            map(model => new GridModel<ReportTemplateListResponse>(
                model.data.map(row => ({ ...row, isLocked: row.isDefault || !row.canManage })),
                model.count
            ))
        );
    }

    getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
        return this.http.get<{ [property: string]: ColumnDetails }>(
            this.getApiUrl(`${this.controllerName}/GetFieldConfiguration`)
        ).pipe(catchError(error => throwError(() => error)));
    }

    export(request: GridExport, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
        return this.http.post(
            this.getApiUrl(`${this.controllerName}/Export`),
            request,
            { responseType: 'blob', observe: 'response', headers }
        );
    }

    getReportTemplateById(id: string): Observable<ReportTemplate> {
        return this.getById(id).pipe(map(dto => this.convertToReportTemplate(dto)));
    }

    getReportTemplatesByEntityType(entityType?: string): Observable<ReportTemplate[]> {
        const url = entityType
            ? this.getApiUrl(`${this.controllerName}/GetByEntityType?entityType=${encodeURIComponent(entityType)}`)
            : this.getApiUrl(`${this.controllerName}/GetByEntityType`);
        return this.http.get<Template[]>(url).pipe(
            map(dtos => dtos.map(dto => this.convertToReportTemplate(dto)))
        );
    }

    convertToReportTemplate(dto: Template): ReportTemplate {
        let parsedTemplateJson: Partial<TemplateJson>;
        try {
            if (typeof dto.templateJson === 'string') {
                parsedTemplateJson = JSON.parse(dto.templateJson) as Partial<TemplateJson>;
            } else {
                parsedTemplateJson = dto.templateJson;
            }
        } catch (error) {
            parsedTemplateJson = {};
        }

        return {
            id: dto.id,
            name: dto.name,
            entityType: dto.entityType,
            organizationLevelId: dto.organizationLevelId,
            templateJson: {
                status: parsedTemplateJson.status ?? dto.isActive,
                layoutHtml: parsedTemplateJson.layoutHtml || '',
                assets: parsedTemplateJson.assets || [],
                declaredFields: {
                    bound: parsedTemplateJson.declaredFields?.bound || [],
                    userInputs: this.convertUserInputs(parsedTemplateJson.declaredFields?.userInputs) || {}
                },
                supportedFilters: parsedTemplateJson.supportedFilters || [],
                printOptions: parsedTemplateJson.printOptions || this.getDefaultPrintOptions()
            },
            isActive: dto.isActive,
            isShared: dto.isShared ?? false,
            isDefault: dto.isDefault ?? false,
            createdBy: dto.createdBy || '',
            createdByUserId: dto.createdByUserId,
            canManage: dto.canManage ?? false,
            createdDate: dto.createdDate || ''
        };
    }

    renameTemplate(template: ReportTemplate, newName: string): Observable<Object> {
        const guardError = this.guardTemplateContent(template);
        if (guardError) return guardError;
        const payload = this.buildUpdatePayload(
            { ...template, name: newName },
            template.templateJson.layoutHtml,
            template.templateJson.printOptions
        );
        return this.update(payload);
    }

    cloneTemplate(source: ReportTemplate, allNames: string[], chosenName?: string): Observable<Template> {
        const guardError = this.guardTemplateContent(source);
        if (guardError) return guardError;
        let copyName: string;
        if (chosenName) {
            copyName = chosenName;
        } else {
            const existingNames = new Set(allNames);
            copyName = this.translateService.getDynamicTranslation('{{viewName}} - Copy', { viewName: source.name });
            let i = 1;
            while (existingNames.has(copyName)) {
                copyName = this.translateService.getDynamicTranslation('{{viewName}} - Copy ({{index}})', { viewName: source.name, index: ++i });
            }
        }
        const payload = this.buildCreatePayload(
            source,
            copyName,
            source.templateJson.layoutHtml,
            source.templateJson.printOptions,
            false
        );
        return this.create(payload);
    }

    setShared(template: ReportTemplate, isShared: boolean): Observable<Object> {
        const guardError = this.guardTemplateContent(template);
        if (guardError) return guardError;
        const payload = this.buildUpdatePayload(
            { ...template, isShared },
            template.templateJson.layoutHtml,
            template.templateJson.printOptions
        );
        return this.update(payload);
    }

    private guardTemplateContent(template: ReportTemplate): Observable<never> | null {
        if (template.templateJson?.layoutHtml?.trim()) return null;
        return throwError(() => new Error('Template content is empty or failed to parse — aborting to prevent data loss'));
    }

    buildUpdatePayload(template: ReportTemplate, layoutHtml: string, printOptions: TemplatePrintOptions): Template {
        return {
            id: template.id,
            name: template.name,
            entityType: template.entityType,
            organizationLevelId: template.organizationLevelId,
            isActive: template.isActive,
            isShared: template.isShared,
            isDefault: template.isDefault,
            templateJson: this.buildReportTemplateJson(template.id, template, layoutHtml, printOptions)
        };
    }

    buildCreatePayload(
        source: ReportTemplate,
        name: string,
        layoutHtml: string,
        printOptions: TemplatePrintOptions,
        isShared: boolean
    ): CreatePayload<Template> {
        return {
            name,
            entityType: source.entityType,
            organizationLevelId: source.organizationLevelId,
            isActive: true,
            isShared,
            isDefault: false,
            templateJson: this.buildReportTemplateJson('', { ...source, name }, layoutHtml, printOptions)
        };
    }

    private buildReportTemplateJson(
        id: string,
        template: ReportTemplate,
        layoutHtml: string,
        printOptions: TemplatePrintOptions
    ): ReportTemplateJson {
        return {
            ...template.templateJson,
            id,
            name: template.name,
            entityType: template.entityType,
            layoutHtml,
            printOptions
        };
    }

    private getDefaultPrintOptions(): TemplatePrintOptions {
        return {
            pageSize: 'A4',
            margins: '0mm',
            headerHtml: '',
            footerHtml: ''
        };
    }

    private convertUserInputs(
        userInputsObj: Record<string, { label?: string; type?: string; default?: string | boolean }> | undefined
    ): { [key: string]: UserInputField } {
        if (!userInputsObj) return {};

        const converted: { [key: string]: UserInputField } = {};
        Object.entries(userInputsObj).forEach(([key, value]) => {
            converted[key] = {
                label: value.label || key,
                type: (value.type as UserInputField['type']) || 'text',
                default: value.default || ''
            };
        });

        return converted;
    }
}
